import { Button, Col, Form, Row, Tooltip } from 'antd';
import React, { forwardRef, ReactElement, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CloseOutlined as Close } from '@ant-design/icons';
import { RootState } from '../../../store';
import { GUTTOR_SPACE, VALIDATE_MESSAGES } from '../../constants/commonConstants';
import { NewAdmissionState } from '../../../store/models/newAdmissionModel';
import withRouterForwardRef from '../hoc/withRouterForwardRef';
import { centeredNotification } from '../../../utils/utility';
import { ID_CARD_NAV } from '../../constants/crmConstants';
import { ReactComponent as RoundTickIcon } from '../../../assets/images/svg/i_round_tick.svg';
import { setGetHealthIdCard } from '../../../store/actions/newAdmissionAction';
import './IDCardModal.less';
import { resetIdCardState } from '../../../store/actions/idCardAction';

const IDCardModal = forwardRef((
  { showPreviousButton = false,
  }: any,
  ref: any,
): ReactElement | null => {

  const {
    getHealthIdCard, healthCardType, createAbhaAlreadyExist,
  } = useSelector((state: RootState): NewAdmissionState => state.newAdmission);
  const { callback } = useSelector((state: RootState): any => state.idCard);
  const [current, setCurrent] = useState(0);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [idCardNav, setIdCardNav] = useState<any>([]);
  const [isSuccessStep, setIsSuccessStep] = useState<any>(false);
  const [enableNextButton, setEnableNextButton] = useState(true);
  const [IdCardForm] = Form.useForm();
  const dispatch = useDispatch();

  useEffect((): any => {
    setIdCardNav(ID_CARD_NAV[healthCardType]);
    setCurrent(0);
  }, [healthCardType]);

  useEffect((): any => {
    if (createAbhaAlreadyExist) {
      setIsSubmitLoading(false);
    }
  }, [createAbhaAlreadyExist]);

  const next = (payload: any): void => {
    setIsSubmitLoading(true);
    IdCardForm.validateFields().then((): void => {
      if (idCardNav[current].submitHandler) {
        dispatch(idCardNav[current].submitHandler({ payload, onSuccess, onError, submitUrl: idCardNav[current].submitUrl }));
      }
    });
  };

  const goNext = (skipStep: any): void => {
    if (current === idCardNav.length - 1 && !idCardNav[current]?.isSuccessStep) {
      callback();
    } else {
      setCurrent(skipStep ? current + 2 : current + 1);
      if (current < idCardNav.length - 2) {
        setEnableNextButton(false);
      } else {
        if (idCardNav[current + 1]?.isSuccessStep !== undefined) {
          setIsSuccessStep(idCardNav[current + 1]?.isSuccessStep);
          setEnableNextButton(false);
        } else {
          setIsSuccessStep(true);
        }
      }
    }
  };

  const prev = (): void => {
    setCurrent(current - 1);
  };

  const onSuccess = (skipStep: any): void => {
    setIsSubmitLoading(false);
    setEnableNextButton(true);
    goNext(skipStep);
  };

  const onError = (errorMsg: string): void => {
    setIsSubmitLoading(false);
    centeredNotification(errorMsg);
  };

  const resendOTP = (payload: any): any => {
    if (idCardNav && idCardNav[current - 1]) {
      dispatch(idCardNav[current - 1].submitHandler({ payload, submitUrl: idCardNav[current - 1].submitUrl }));
    }
  };

  const onClose = (): void => {
    dispatch(setGetHealthIdCard(false));
    dispatch(resetIdCardState());
    callback();
  };

  if (!idCardNav?.length) {
    return null;
  }

  const { component: ContentComponent, title, nextButtonName }: any = idCardNav[current];

  return (
    <React.Fragment>
      <div className={getHealthIdCard ? 'back-drop' : ''} />
      <div
        data-testid="Id-card-modal"
        className={`${!isSuccessStep ? 'id-card-modal' : 'id-card-modal id-card-modal-success'} minimize-modal-wrapper open ${!getHealthIdCard ? 'animate-bottom' : ''}`}
      >
        <div className={`modal-header ${isSuccessStep ? 'text-success' : ''}`}>
          {isSuccessStep ? <RoundTickIcon className="fill-success" /> : ''}
          <h4>{`${title}`}</h4>
          <Tooltip title="Close document window">
            <span
              onClick={onClose} className="close-btn ant-modal-close-x" data-testid="minimize-icon">
              <Close />
            </span>
          </Tooltip>
        </div>
        <div className="modal-body scrollbar">
          <Form
            layout="vertical"
            form={IdCardForm}
            validateMessages={VALIDATE_MESSAGES}
            scrollToFirstError={true}
            onFinish={next}
          >
            <>
              {
                ContentComponent ? <ContentComponent
                  form={IdCardForm}
                  next={next}
                  previous={prev}
                  resendOTP={resendOTP}
                  setEnableNextButton={setEnableNextButton}
                  otpTimeout={idCardNav[current]?.otpTimeout}
                /> : null
              }
            </>
            {isSuccessStep ? null : <div className="steps-action">
              <Row gutter={GUTTOR_SPACE}>
                {showPreviousButton && <Col md={4}>
                  {current > 0 && (
                    <Button onClick={(): void => prev()} data-testid="prev">
                      Previous
                    </Button>
                  )}
                </Col>
                }
                <Col md={showPreviousButton ? 20 : 24} className="text-center">
                  <Button
                    htmlType="submit"
                    type={'primary'}
                    disabled={enableNextButton}
                    loading={isSubmitLoading}
                    data-testid="next"
                    >
                    {nextButtonName}
                  </Button>
                </Col>
              </Row>
            </div>
            }
          </Form>
        </div>
      </div>
    </React.Fragment >
  );
});

export default withRouterForwardRef(IDCardModal);
